<template>
  <div
    class="roleplay-detail bg-base-100 text-base-content flex flex-col overflow-hidden h-full"
  >
    <ThreadsNav
      show-back
      :enableSwitchMate="false"
      class="border-b"
      :show-center="false"
    >
      <template #left>
        <div
          v-if="!loading"
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              isSaving ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span>
            {{ $t(`User.settings${isSaving ? "Unsaved" : "Saved"}`) }}
          </span>
        </div>
      </template>
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>

    <!-- Question content -->
    <div class="p-4 flex-grow overflow-auto pb-24">
      <div v-if="loading" class="flex justify-center items-center h-full">
        <div class="loading loading-ring loading-lg"></div>
      </div>

      <template v-else>
        <div class="card bg-base-200 shadow-md mb-4">
          <div class="card-body p-4">
            <h2 class="text-base font-bold mb-2">{{ question?.text }}</h2>
            <p class="text-base-content/70 text-xs">
              {{ question?.explanation }}
            </p>
          </div>
        </div>

        <!-- Answer form -->

        <VanCellGroup class="rounded-lg overflow-hidden" :border="false">
          <!-- 名字 -->
          <VanField
            label="当前身份"
            :model-value="useName(mateStore.mateIdMap.get(answer.mateId)?.name)"
            :placeholder="$t('Message.none')"
            :rules="[
              { required: true, message: $t('User.mate.basicInfo.rules.name') },
            ]"
            required
            is-link
            readonly
            @click="clickMateSelect"
          />
          <!-- 类型 -->
          <VanField
            v-if="answer.text"
            label="谁能看到"
            :model-value="visibilityText"
            :placeholder="$t('Message.none')"
            :rules="[
              { required: true, message: $t('User.mate.basicInfo.rules.type') },
            ]"
            is-link
            readonly
            required
            @click="clickVisibilitySelect"
          />
          <CustomRichTextCell
            v-if="question"
            title="填写回答"
            :ai-type="AIWriteType.Roleplay"
            :options="{
              mateId: answer.mateId,
              questionId: question.id,
            }"
            :content="answer.text"
            :cb="onAnswerUpdate"
          />

          <VanCell v-if="answer.mateId">
            <VoiceGenerate
              :mate="mateStore.mateIdMap.get(answer.mateId)!"
              :text="removeHtmlTag(answer.text || '')"
              tone-type="voice"
              :active-media="answer.audioMedia"
              @generate="onGenerateAudio"
            />
          </VanCell>
        </VanCellGroup>
      </template>
    </div>

    <!-- Action buttons -->
    <div
      class="absolute bottom-0 p-4 border-t border-base-200 bg-base-200 left-0 right-0"
      v-if="answer.text && answer.id"
    >
      <div class="flex gap-4 justify-center">
        <button
          class="btn btn-outline"
          :disabled="!isSaved"
          @click="showShare = true"
        >
          分享到饼友圈
        </button>
        <!-- <button
          class="btn btn-primary"
          @click="generatePoster"
          :disabled="!isSaved"
        >
          生成小红书海报
        </button> -->
      </div>
    </div>
    <VisibilitySelectModal
      v-model:show="showVisibilitySelect"
      :visibility="answer.visibility"
      @update:visibility="onVisibilityChange"
    />

    <ShareWithMoment
      v-model:show="showShare"
      :share-id="answer.id"
      :mate-uuid="mateStore.userMate.uuid"
      :share-type="'roleplayAnswer'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import { showToast } from "vant";
import {
  ApiGetRoleplayQuestion,
  ApiCreateMateRoleplayAnswer,
  ApiUpdateMateRoleplayAnswer,
} from "@/apis/roleplay";
import {
  Mate,
  MateRoleplayAnswer,
  Media,
  RoleplayQuestion,
  Visibility,
} from "@/types/entity";
import { useMate } from "@/stores/mate";
import { useUI } from "@/stores/ui";
import { useAllVisibility, useName } from "@/use/common";
import { useDebounceFn } from "@vueuse/core";
import { removeHtmlTag } from "@/tools/text";
import { AIWriteType } from "@/enums/mateEnums";

const mateStore = useMate();
const route = useRoute();
const loading = ref(true);
const isSaved = ref(true);

const isSaving = ref(false);
const question = ref<RoleplayQuestion>();

const answer = ref<MateRoleplayAnswer>({} as MateRoleplayAnswer);

const answerMap = ref<Record<number, MateRoleplayAnswer>>({});

const questionUuid = computed(() => route.params.uuid);
const mateId = ref(Number(route.query.mateId));
const VISIBILITIES = useAllVisibility();
const visibilityText = computed(() => {
  return VISIBILITIES.find((v) => v.value === answer.value.visibility)?.text;
});

const onGenerateAudio = (media?: Media) => {
  if (!media) return;

  answer.value.audioMediaId = media?.id;
  answer.value.audioMedia = media;
  saveAnswer();
};
// Save answer to server
const saveAnswer = async () => {
  try {
    let response;
    const { text, visibility, audioMediaId, id } = answer.value;
    if (id) {
      if (!text) answer.value.text = "";
      response = await ApiUpdateMateRoleplayAnswer(id, {
        text,
        visibility,
        audioMediaId: audioMediaId || 0,
      });
      if (response.item) {
        answerMap.value[answer.value.mateId] = answer.value;
      }
    } else {
      if (!answer.value.text) return (isSaved.value = true);
      response = await ApiCreateMateRoleplayAnswer(answer.value);
      if (response.item) {
        answer.value.id = response.item.id;
        answerMap.value[answer.value.mateId] = answer.value;
      }
    }

    isSaved.value = true;
  } catch (error) {
    console.error("Failed to save answer", error);
    showToast("保存失败，请重试");
  }
};

const uiStore = useUI();
const clickMateSelect = () => {
  uiStore.popMateSelector({
    mateOnly: true,
    switchMate: false,
    exclude: [answer.value.mateId],
    title: "选择当前身份",
    callback: (mate: Mate) => {
      uiStore.showMateSelector = false;
      initAnswer(mate.id);
    },
  });
};

const showVisibilitySelect = ref(false);
const clickVisibilitySelect = () => {
  showVisibilitySelect.value = true;
};

const onVisibilityChange = (visibility: Visibility) => {
  showVisibilitySelect.value = false;
  answer.value.visibility = visibility;
  saveAnswer();
};

const showShare = ref(false);

const debouncedSaveAnswer = useDebounceFn(async () => {
  try {
    await saveAnswer();
    isSaving.value = false;
  } catch (error) {
    console.error("Error saving answer:", error);
  }
}, 1000);

watch(
  () => answer.value.text,
  (val, oldVal) => {
    if (oldVal === undefined || val === undefined) return;
    if (val !== oldVal) {
      isSaving.value = true;
      debouncedSaveAnswer();
    }
  }
);

const initAnswer = (mateId: number) => {
  if (answerMap.value[mateId]) {
    answer.value = {} as MateRoleplayAnswer;
    nextTick(() => {
      answer.value = answerMap.value[mateId];
    });
  } else {
    answer.value = {
      mateId,
      questionId: question.value!.id,
      visibility: 1,
      text: "",
    } as MateRoleplayAnswer;
  }
};

const onAnswerUpdate = (content: string) => {
  answer.value.text = content;
};

// Fetch question and answer data
onMounted(async () => {
  try {
    loading.value = true;

    // Fetch question details
    const res = await ApiGetRoleplayQuestion(questionUuid.value as string);
    question.value = res.item;

    answerMap.value = res.item.answerzz.reduce((acc, answer) => {
      acc[answer.mateId] = answer;
      return acc;
    }, {} as Record<number, MateRoleplayAnswer>);

    if (!mateId.value) {
      mateId.value = mateStore.list[0].id!;
    }

    initAnswer(Number(mateId.value));

    // Check if user has already answered
    try {
      // const res = await ApiGetMateRoleplayAnswer(questionId.value);
    } catch (error) {
      console.log("No existing answer found");
    }
  } catch (error) {
    console.error("Failed to load question details", error);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.textarea:focus {
  border-color: var(--color-primary);
}

.btn-outline {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.btn-outline:hover {
  background-color: var(--color-primary);
  color: white;
}
</style>
